/**
 * Component-Group ↔ legacy `category` translation layer.
 *
 * Today the BE filter param on `listComponents` and
 * `listAssetComponentModels` (expanded) is `category=<csv>` — a free-
 * text CSV against `asset_component_models.category` (VARCHAR(100),
 * typically the lowercased part-axis label like `rotor_blade`,
 * `tower`, `foundation`).
 *
 * The FE filter UX surfaces the canonical seven `global_component_groups`
 * rows (groupCode = `ROTOR_BLADE`, `TOWER`, …) instead of the free-text
 * column, because:
 *   - the rollup the BE returns mixes the seven groups with any
 *     drift values that crept into `asset_component_models.category`
 *     (capitalisation, typos);
 *   - users only ever filter against the canonical seven anyway.
 *
 * Until the BE accepts `componentGroupId` natively (proposed in
 * docs/backend-requirements/082-…-component-group-filter.md), we
 * translate FE-side: `componentGroupId` → group → groupCode →
 * `groupCode.toLowerCase()` → `category` wire param.
 *
 * Lossy direction (groupCode → category lowercased) matches the
 * seed data verbatim. If a tenant has a row with a non-canonical
 * `category` value, that row is unmatched by the Component Group
 * filter — by design.
 */
import type { ComponentGroup } from "../queries/types.js";

/** Convert a groupCode (`"ROTOR_BLADE"`) to the legacy category
 *  wire-string (`"rotor_blade"`). */
export function groupCodeToCategoryWire(groupCode: string): string {
  return groupCode.toLowerCase();
}

/** Resolve a list of component-group UUIDs to the CSV of legacy
 *  `category` wire-strings the BE accepts today. Stable, alphabetical. */
export function componentGroupIdsToCategoryCsv(
  ids: readonly string[],
  groups: readonly ComponentGroup[],
): string[] {
  if (ids.length === 0) return [];
  const byId = new Map(groups.map((g) => [g.id, g]));
  const out = new Set<string>();
  for (const id of ids) {
    const g = byId.get(id);
    if (g) out.add(groupCodeToCategoryWire(g.groupCode));
  }
  return [...out].sort((a, b) => a.localeCompare(b));
}

/** Render the human-readable label for a component group (English
 *  i18n today; `name.en` is the canonical surface). Falls back to the
 *  groupCode when the i18n key is missing. */
export function componentGroupLabel(group: ComponentGroup): string {
  return group.name.en ?? group.groupCode;
}

/** Lookup a component group's display name by its UUID, returning
 *  `null` when the id is unknown (component-groups list not yet
 *  loaded, or the group was deleted server-side). */
export function findGroupNameById(
  id: string | null | undefined,
  groups: readonly ComponentGroup[],
): string | null {
  if (!id) return null;
  const g = groups.find((g) => g.id === id);
  return g ? componentGroupLabel(g) : null;
}
